/**
 * MyTasksView — unified inbox of items assigned to the current user.
 * Merges incidents + actions into a single sorted list with a type chip.
 * Clicking a row navigates to the per-site Incidents or Actions view
 * with that item pre-selected (full detail panel lives there).
 */
import React, { useMemo, useState } from 'react';
import { Inbox, AlertOctagon, CheckSquare, ChevronRight } from 'lucide-react';
import C from '../../constants/colors';

const INCIDENT_STATUS = {
  OPEN:        { label: 'Open',        color: C.red },
  IN_PROGRESS: { label: 'In Progress', color: C.amber },
  CLOSED:      { label: 'Closed',      color: C.textMuted },
};
const ACTION_STATUS = {
  OPEN:        { label: 'Open',        color: C.red },
  IN_PROGRESS: { label: 'In Progress', color: C.amber },
  COMPLETED:   { label: 'Completed',   color: C.green },
};

function formatAge(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function dueDateMeta(dueDate, status) {
  if (!dueDate || status === 'COMPLETED' || status === 'CLOSED') return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate); due.setHours(0, 0, 0, 0);
  const diffDays = Math.round((due.getTime() - today.getTime()) / 86_400_000);
  if (diffDays < 0)   return { label: `Overdue ${-diffDays}d`, color: C.red };
  if (diffDays === 0) return { label: 'Due today',             color: C.amber };
  if (diffDays <= 3)  return { label: `Due ${diffDays}d`,      color: C.amber };
  return null;
}

export default function MyTasksView({ incidents, actions, currentUserId, onOpenIncident, onOpenAction }) {
  const [filterStatus, setFilterStatus] = useState('OPEN'); // OPEN means "not done": OPEN + IN_PROGRESS
  const [filterType, setFilterType] = useState('ALL');       // ALL | INCIDENTS | ACTIONS

  // Build the merged list. Each item has { type, raw, status, isDone, dueDate, sortDate }
  const items = useMemo(() => {
    const myInc = (incidents || [])
      .filter(i => i.assignedUserId === currentUserId)
      .map(i => ({
        type: 'INCIDENT', raw: i,
        status: i.status, isDone: i.status === 'CLOSED',
        sortDate: i.createdAt, dueDate: null,
      }));
    const myAct = (actions || [])
      .filter(a => a.assignedUserId === currentUserId)
      .map(a => ({
        type: 'ACTION', raw: a,
        status: a.status, isDone: a.status === 'COMPLETED',
        sortDate: a.dueDate || a.createdAt, dueDate: a.dueDate,
      }));
    return [...myInc, ...myAct].sort((a, b) => {
      // Open items first, then by date desc
      if (a.isDone !== b.isDone) return a.isDone ? 1 : -1;
      return new Date(b.sortDate) - new Date(a.sortDate);
    });
  }, [incidents, actions, currentUserId]);

  const filtered = useMemo(() => items.filter(it => {
    if (filterType === 'INCIDENTS' && it.type !== 'INCIDENT') return false;
    if (filterType === 'ACTIONS'   && it.type !== 'ACTION')   return false;
    if (filterStatus === 'ALL') return true;
    if (filterStatus === 'OPEN') return !it.isDone;            // OPEN + IN_PROGRESS
    if (filterStatus === 'DONE') return it.isDone;             // CLOSED / COMPLETED
    return it.status === filterStatus;
  }), [items, filterStatus, filterType]);

  const counts = useMemo(() => ({
    total:   items.length,
    open:    items.filter(it => !it.isDone).length,
    done:    items.filter(it => it.isDone).length,
    inc:     items.filter(it => it.type === 'INCIDENT' && !it.isDone).length,
    act:     items.filter(it => it.type === 'ACTION'   && !it.isDone).length,
  }), [items]);

  const pill = (active, color) => ({
    padding: '6px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
    border: `1px solid ${active ? color : C.border}`,
    background: active ? color + '22' : 'transparent',
    color: active ? color : C.textSecondary,
  });

  function handleRowClick(item) {
    if (item.type === 'INCIDENT') {
      onOpenIncident?.(item.raw.id, item.raw.siteId);
    } else {
      onOpenAction?.(item.raw.id, item.raw.siteId);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, flexShrink: 0, background: C.surface }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <Inbox size={20} color={C.blue} />
          <h2 style={{ fontSize: '20px', fontWeight: 700, color: C.textPrimary, margin: 0 }}>My Tasks</h2>
          <span style={{ fontSize: '12px', color: C.textMuted, marginLeft: 'auto' }}>{filtered.length} of {counts.total}</span>
        </div>

        {/* Status pills */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
          <button onClick={() => setFilterStatus('OPEN')} style={pill(filterStatus === 'OPEN', C.blue)}>
            Open <span style={{ opacity: 0.7, marginLeft: '4px' }}>· {counts.open}</span>
          </button>
          <button onClick={() => setFilterStatus('ALL')} style={pill(filterStatus === 'ALL', C.textSecondary)}>
            All <span style={{ opacity: 0.7, marginLeft: '4px' }}>· {counts.total}</span>
          </button>
          <button onClick={() => setFilterStatus('DONE')} style={pill(filterStatus === 'DONE', C.green)}>
            Done <span style={{ opacity: 0.7, marginLeft: '4px' }}>· {counts.done}</span>
          </button>
        </div>

        {/* Type pills */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          <button onClick={() => setFilterType('ALL')} style={pill(filterType === 'ALL', C.textSecondary)}>
            All types
          </button>
          <button onClick={() => setFilterType('INCIDENTS')} style={pill(filterType === 'INCIDENTS', C.red)}>
            Incidents <span style={{ opacity: 0.7, marginLeft: '4px' }}>· {counts.inc}</span>
          </button>
          <button onClick={() => setFilterType('ACTIONS')} style={pill(filterType === 'ACTIONS', C.blue)}>
            Actions <span style={{ opacity: 0.7, marginLeft: '4px' }}>· {counts.act}</span>
          </button>
        </div>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '60px 20px', color: C.textMuted, fontSize: '13px', textAlign: 'center' }}>
            {counts.total === 0 ? 'You have no tasks assigned to you.' : 'No tasks match the current filters.'}
          </div>
        ) : (
          <div>
            {filtered.map(item => {
              const isIncident = item.type === 'INCIDENT';
              const Icon = isIncident ? AlertOctagon : CheckSquare;
              const meta = isIncident ? INCIDENT_STATUS[item.status] : ACTION_STATUS[item.status];
              const typeColor = isIncident ? C.red : C.blue;
              const due = dueDateMeta(item.dueDate, item.status);
              const label = isIncident
                ? `${item.raw.incidentType?.replace(/_/g, ' ')} · ${item.raw.description}`
                : item.raw.description;
              return (
                <div key={`${item.type}-${item.raw.id}`}
                  onClick={() => handleRowClick(item)}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '14px 24px',
                    borderLeft: `3px solid ${due?.color === C.red ? C.red : meta.color}`,
                    borderBottom: `1px solid ${C.border}`,
                    cursor: 'pointer' }}>
                  {/* Type chip */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '4px 10px', borderRadius: '6px',
                    background: typeColor + '22', color: typeColor,
                    fontSize: '10px', fontWeight: 700, textTransform: 'uppercase',
                    flexShrink: 0, minWidth: '90px' }}>
                    <Icon size={12} />
                    {item.type}
                  </div>

                  {/* Body */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', color: C.textPrimary, fontWeight: 500, marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {label}
                    </div>
                    <div style={{ fontSize: '11px', color: C.textMuted, display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {item.raw.siteName && <span>{item.raw.siteName}</span>}
                      <span>{formatAge(item.raw.createdAt)}</span>
                      {due && <span style={{ color: due.color, fontWeight: 700 }}>· {due.label}</span>}
                    </div>
                  </div>

                  {/* Status + chevron */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                    <span style={{ fontSize: '10px', fontWeight: 700, color: meta.color,
                      padding: '3px 8px', borderRadius: '4px',
                      background: meta.color + '22', textTransform: 'uppercase' }}>
                      {meta.label}
                    </span>
                    <ChevronRight size={16} color={C.textMuted} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
